import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import {FormattedMessage} from 'react-intl';

import Box from '../box/box.jsx';
import CloseButton from '../close-button/close-button.jsx';
import Spinner from '../spinner/spinner.jsx';
import {AlertLevels} from '../../lib/alerts/index.jsx';

import styles from './alert.css';

const closeButtonColors = {
    [AlertLevels.SUCCESS]: CloseButton.COLOR_GREEN,
    [AlertLevels.WARN]: CloseButton.COLOR_ORANGE,
    [AlertLevels.INFO]: CloseButton.COLOR_NEUTRAL
};

const AlertComponent = ({
    content,
    closeButton,
    duration,
    extensionName,
    iconSpinner,
    iconURL,
    level,
    showDownload,
    showSaveNow,
    onCloseAlert,
    onDownload,
    onSaveNow,
    onReconnect,
    showReconnect
}) => {
    // Auto-dismiss countdown. Lives here (rather than in the action creator) so
    // it can pause while the pointer is over the toast — otherwise a message can
    // vanish mid-read. duration = 0/undefined means "stay until dismissed".
    const [paused, setPaused] = React.useState(false);
    const remainingRef = React.useRef(duration);
    const startedAtRef = React.useRef(null);

    const handlePause = React.useCallback(() => setPaused(true), []);
    const handleResume = React.useCallback(() => setPaused(false), []);

    React.useEffect(() => {
        // No countdown for alerts without a duration (e.g. the standard
        // project-save alerts) — they keep their existing behaviour.
        if (!duration || paused) return () => {};
        startedAtRef.current = Date.now();
        const timer = setTimeout(onCloseAlert, remainingRef.current);
        return () => {
            clearTimeout(timer);
            // Bank the time already elapsed so resuming continues where it left off.
            remainingRef.current -= (Date.now() - startedAtRef.current);
        };
    }, [duration, paused, onCloseAlert]);

    return (
        <Box
            className={classNames(styles.alert, styles[level])}
            onMouseEnter={duration ? handlePause : null}
            onMouseLeave={duration ? handleResume : null}
        >
            {/* TODO: implement Rtl handling */}
            {(iconSpinner || iconURL) && (
                <div className={styles.iconSection}>
                    {iconSpinner && (
                        <Spinner
                            className={styles.alertSpinner}
                            level={level}
                        />
                    )}
                    {iconURL && (
                        <img
                            className={styles.alertIcon}
                            src={iconURL}
                        />
                    )}
                </div>
            )}
            <div className={styles.alertMessage}>
                {extensionName ? (
                    <FormattedMessage
                        defaultMessage="Scratch lost connection to {extensionName}."
                        description="Message indicating that an extension peripheral has been disconnected"
                        id="gui.alerts.lostPeripheralConnection"
                        values={{
                            extensionName: (
                                `${extensionName}`
                            )
                        }}
                    />
                ) : content}
            </div>
            <div className={styles.alertButtons}>
                {showSaveNow && (
                    <button
                        className={styles.alertConnectionButton}
                        onClick={onSaveNow}
                    >
                        <FormattedMessage
                            defaultMessage="Try Again"
                            description="Button to try saving again"
                            id="gui.alerts.tryAgain"
                        />
                    </button>
                )}
                {showDownload && (
                    <button
                        className={styles.alertConnectionButton}
                        onClick={onDownload}
                    >
                        <FormattedMessage
                            defaultMessage="Download"
                            description="Button to download project locally"
                            id="gui.alerts.download"
                        />
                    </button>
                )}
                {showReconnect && (
                    <button
                        className={styles.alertConnectionButton}
                        onClick={onReconnect}
                    >
                        <FormattedMessage
                            defaultMessage="Reconnect"
                            description="Button to reconnect the device"
                            id="gui.connection.reconnect"
                        />
                    </button>
                )}
                {closeButton && (
                    <Box
                        className={styles.alertCloseButtonContainer}
                    >
                        <CloseButton
                            className={classNames(styles.alertCloseButton)}
                            color={closeButtonColors[level]}
                            size={CloseButton.SIZE_LARGE}
                            onClick={onCloseAlert}
                        />
                    </Box>
                )}
            </div>
            {/* Countdown bar — shows how long until the toast closes itself, and
            visibly freezes while hovered so the pause is discoverable. */}
            {duration ? (
                <div
                    className={classNames(styles.alertProgress, styles[`progress-${level}`])}
                    style={{
                        animationDuration: `${duration}ms`,
                        animationPlayState: paused ? 'paused' : 'running'
                    }}
                />
            ) : null}
        </Box>
    );
};

AlertComponent.propTypes = {
    closeButton: PropTypes.bool,
    content: PropTypes.oneOfType([PropTypes.element, PropTypes.string]),
    duration: PropTypes.number,
    extensionName: PropTypes.string,
    iconSpinner: PropTypes.bool,
    iconURL: PropTypes.string,
    level: PropTypes.string,
    onCloseAlert: PropTypes.func.isRequired,
    onDownload: PropTypes.func,
    onReconnect: PropTypes.func,
    onSaveNow: PropTypes.func,
    showDownload: PropTypes.func,
    showReconnect: PropTypes.bool,
    showSaveNow: PropTypes.bool
};

AlertComponent.defaultProps = {
    level: AlertLevels.WARN
};

export default AlertComponent;
